
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { supabase } from '@/integrations/supabase/client';
import { Copy, ExternalLink } from 'lucide-react';

interface TypeformIntegrationProps {
  webhookUrl: string;
}

const TypeformIntegration = ({ webhookUrl }: TypeformIntegrationProps) => {
  const { toast } = useToast();
  const [testMode, setTestMode] = useState(false);
  const [testData, setTestData] = useState('');
  
  const form = useForm({
    defaultValues: {
      webhookSecret: '',
    }
  });

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: "Copied!",
      description: "Webhook URL copied to clipboard",
    });
  };

  const submitTestData = async () => {
    try {
      if (!testData) {
        toast({
          title: "Error",
          description: "Please enter test data",
          variant: "destructive",
        });
        return;
      }
      
      const parsedData = JSON.parse(testData);
      
      // Manually process the webhook payload for testing
      const response = await fetch('/api/typeform-webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parsedData),
      });
      
      if (response.ok) {
        toast({
          title: "Success",
          description: "Test data processed successfully",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to process test data",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error submitting test data:', error);
      toast({
        title: "Error",
        description: "Invalid JSON data",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-white">Typeform Integration</CardTitle>
        <CardDescription className="text-gray-300">
          Connect Typeform to automatically add new speakers from submissions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-white">Webhook URL</h3>
          <p className="text-sm text-gray-300 mb-2">
            Add this URL to your Typeform integration settings to receive form submissions:
          </p>
          <div className="flex items-center gap-2">
            <Input 
              value={webhookUrl} 
              readOnly 
              className="bg-black/30 text-gray-300 border-purple-500/30"
            />
            <Button 
              variant="outline" 
              size="icon" 
              onClick={copyWebhookUrl}
              className="text-white hover:bg-purple-500/30"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-lg font-medium text-white mb-4">Typeform Setup Guide</h3>
          <ol className="list-decimal list-inside space-y-3 text-gray-300">
            <li>Create a Typeform that collects speaker information (name, title, company, bio, etc.)</li>
            <li>In your Typeform workspace, go to the form's settings and find "Integrations"</li>
            <li>Select "Webhooks" integration</li>
            <li>Add the webhook URL from above</li>
            <li>Make sure to include form fields for all speaker information your site displays</li>
            <li>Test the integration by submitting a test form</li>
          </ol>
          
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="text-white hover:bg-purple-500/30"
              onClick={() => window.open('https://www.typeform.com/help/a/webhooks-360029573471/', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Typeform Webhooks Documentation
            </Button>
          </div>
        </div>
        
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-lg font-medium text-white mb-2">Test Integration</h3>
          <Button 
            variant="outline" 
            className="text-white hover:bg-purple-500/30 mb-3"
            onClick={() => setTestMode(!testMode)}
          >
            {testMode ? "Hide Test Console" : "Show Test Console"}
          </Button>
          
          {testMode && (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                Paste a sample Typeform webhook payload below to test the integration:
              </p>
              <textarea
                className="w-full h-32 p-2 bg-black/30 text-gray-300 border border-purple-500/30 rounded-md font-mono text-sm"
                placeholder='{"form_response": {"answers": [...], "definition": {"fields": [...]} }}'
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
              />
              <Button 
                onClick={submitTestData}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Process Test Data
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TypeformIntegration;
